import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './LandingPage.css';
import { 
  faRocket, 
  faCheckCircle, 
  faUsers, 
  faChartLine,
  faBolt,
  faShieldAlt,
  faFileUpload,
  faRobot,
  faClipboardCheck,
  faStar,
  faClock,
  faBullseye,
  faGem
} from '@fortawesome/free-solid-svg-icons';

const LandingPage = () => {
  const navigate = useNavigate();
  const onShowLogin = () => navigate('/login');
  const featuresSectionRef = useRef(null);
  const ctaSectionRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    const featuresElement = featuresSectionRef.current;
    const ctaElement = ctaSectionRef.current;

    if (featuresElement) {
      observer.observe(featuresElement);
    }
    if (ctaElement) {
      observer.observe(ctaElement);
    }

    return () => {
      if (featuresElement) {
        observer.unobserve(featuresElement);
      }
      if (ctaElement) {
        observer.unobserve(ctaElement);
      }
    };
  }, []);

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
  };

  const features = [
    {
      icon: faRocket,
      title: 'Fast Grading',
      description: 'Grade subjective answers in seconds with AI-powered evaluation',
      color: '#3B82F6'
    },
    {
      icon: faCheckCircle,
      title: 'Accurate Results',
      description: 'Consistent and fair grading based on customizable rubrics',
      color: '#10B981'
    },
    {
      icon: faUsers,
      title: 'Student Management',
      description: 'Organize and track student performance effortlessly',
      color: '#F59E0B'
    },
    {
      icon: faChartLine,
      title: 'Analytics',
      description: 'Detailed insights and performance reports',
      color: '#8B5CF6'
    },
    {
      icon: faBolt,
      title: 'Batch Processing',
      description: 'Process multiple answer sheets simultaneously',
      color: '#EC4899'
    },
    {
      icon: faShieldAlt,
      title: 'Secure & Reliable',
      description: 'Your data is protected with enterprise-grade security',
      color: '#EF4444'
    }
  ];

  return (
    <div className="landing-page">
      {/* Animated Background */}
      <div className="animated-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        <div className="floating-shape shape-5"></div>
      </div>

      {/* Navigation */}
      <nav className="landing-nav" style={{ transform: `translateY(${scrollY > 50 ? '-2px' : '0'})` }}>
        <div className="landing-nav-content">
          <div className="landing-logo">
            <img 
              src={`${process.env.PUBLIC_URL}/Logo-FastGrader.png`} 
              alt="FastGrader Logo" 
              className="landing-logo-img"
            />
            <span className="landing-logo-text">FastGrader</span>
          </div>
          <button className="landing-login-btn" onClick={onShowLogin}>
            Login
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section" style={{ transform: `translateY(${scrollY * 0.1}px)` }}>
        <div className="hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faRocket} />
            <span>Powered by AI</span>
          </div>
          <h1 className="hero-title">
            Grade Subjective Answers
            <span className="hero-highlight"> Faster Than Ever</span>
          </h1>
          <p className="hero-description">
            FastGrader uses advanced AI to evaluate subjective answers quickly and accurately.
            Save hours of grading time while maintaining consistency and fairness.
          </p>
          <div className="hero-buttons">
            <button className="hero-btn-primary" onClick={onShowLogin}>
              Get Started
              <span className="btn-arrow">→</span>
            </button>
            <button className="hero-btn-secondary">
              Learn More
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faBolt} />
              </div>
              <div className="stat-number">10x</div>
              <div className="stat-label">Faster Grading</div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faBullseye} />
              </div>
              <div className="stat-number">99%</div>
              <div className="stat-label">Accuracy</div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="stat-number">24/7</div>
              <div className="stat-label">Available</div>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="visual-card card-1">
            <div className="card-glow"></div>
            <div className="card-icon">
              <FontAwesomeIcon icon={faFileUpload} />
            </div>
            <div className="card-text">Upload Answers</div>
            <div className="card-number">1</div>
          </div>
          <div className="visual-card card-2">
            <div className="card-glow"></div>
            <div className="card-icon">
              <FontAwesomeIcon icon={faRobot} />
            </div>
            <div className="card-text">AI Processing</div>
            <div className="card-number">2</div>
            <div className="processing-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
          <div className="visual-card card-3">
            <div className="card-glow"></div>
            <div className="card-icon">
              <FontAwesomeIcon icon={faClipboardCheck} />
            </div>
            <div className="card-text">Get Results</div>
            <div className="card-number">3</div>
          </div>
          <div className="connecting-line line-1"></div>
          <div className="connecting-line line-2"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" ref={featuresSectionRef}>
        <div className="section-header">
          <h2 className="section-title">Everything You Need</h2>
          <p className="section-subtitle">
            Powerful features to streamline your grading workflow
          </p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="feature-card-landing"
              onMouseMove={handleMouseMove}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div 
                className="feature-icon-landing" 
                style={{ background: `${feature.color}15`, color: feature.color }}
              >
                <FontAwesomeIcon icon={feature.icon} />
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" ref={ctaSectionRef}>
        <div className="cta-sparkles">
          <div className="sparkle sparkle-1">
            <FontAwesomeIcon icon={faGem} />
          </div>
          <div className="sparkle sparkle-2">
            <FontAwesomeIcon icon={faStar} />
          </div>
          <div className="sparkle sparkle-3">
            <FontAwesomeIcon icon={faGem} />
          </div>
          <div className="sparkle sparkle-4">
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
        <div className="cta-content">
          <h2 className="cta-title">Ready to Transform Your Grading?</h2>
          <p className="cta-description">
            Join educators who are saving time and improving accuracy with FastGrader
          </p>
          <button className="cta-button" onClick={onShowLogin}>
            Start Grading Now
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-logo landing-logo">
            <img 
              src={`${process.env.PUBLIC_URL}/Logo-FastGrader.png`} 
              alt="FastGrader Logo" 
              className="landing-logo-img footer-logo-img"
            />
            <span className="landing-logo-text">FastGrader</span>
          </div>
          <p className="footer-text">
            © 2025 FastGrader. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
